import { message } from "antd"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import styled from "styled-components"

import { signInAsAgent } from "../../../../../api"
// import { USER_AUTH_HEADER } from "../../../../../api"
import BookingForm from "../../../../_shared/components/BookingForm"
import LoginOverlay from "../../../../_shared/components/LoginOverlay"
import MapSection from "../../../../_shared/components/Map"
import AppHeading from "../../../../_shared/components/Typography/Heading"
import Paragraph from "../../../../_shared/components/Typography/Paragraph"
import ScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useRestResource from "../../../../_shared/hooks/useRestResource"
import useWindowWidth from "../../../../_shared/hooks/useWindowWidth"

// ----------------------------------------------------------------------------

function EventDetail(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------
    const {
        match: {
            params: { id: eventId },
        },
    } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const { search } = useLocation()
    const agentsAccessCode = new URLSearchParams(search).get("code")

    const vw = useWindowWidth()
    const [{ user, token }, dispatch] = useStateValue()

    const birthDate = user?.birthDate

    const history = useHistory()

    const [loginFulfilled, setLoginFulfilled] = useState(false)
    const [loginLoading, setLoginLoading] = useState(
        agentsAccessCode ? true : false
    )

    const [event, fetchEvent, loading] = useRestResource(
        `/events/as-user`,
        eventId,
        token
    )

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        if (agentsAccessCode) {
            setLoginLoading(true)
            signInAsAgent(eventId, agentsAccessCode)
                .then((res) => {
                    dispatch({
                        type: "SIGN_IN_FULFILLED",
                        payload: res,
                    })
                    setLoginFulfilled(true)
                })
                .catch(() => {
                    message.error("Codice agente errato!")
                })
                .finally(() => {
                    setLoginLoading(false)
                })
        }
        //eslint-disable-next-line
    }, [agentsAccessCode])

    useEffect(() => {
        token && fetchEvent()
        // eslint-disable-next-line
    }, [token])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function getMapHeight() {
        if (vw > 1980) return 15
        else if (vw >= 980) return 25
        else return 50
    }

    function getMapsLink(loc) {
        const { city, address, province } = loc
        const base_link = "https://www.google.com/maps/search/?api=1"
        const location = encodeURIComponent(`${address} ${city} ${province}`)
        return `${base_link}&query=${location}`
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const eventSoldOut =
        (event?.slots?.length === 0 || !event?.bookingsOpen) && !loading

    const body = (
        <main>
            <ScreenTitle
                className="screen-title"
                text="Informazioni sull'evento"
            />
            <div className="content">
                <div className="left">
                    <Paragraph className="paragraph">
                        {event?.description}
                    </Paragraph>
                    {event?.detailUrl && (
                        <img
                            src={event.detailUrl}
                            alt="A picture describing the event"
                            className="detail-image"
                        />
                    )}
                </div>
                <div className="right">
                    {!loading && (
                        <div className="map">
                            <AppHeading boldness="bold" className="map-heading">
                                Indicazioni stradali
                            </AppHeading>
                            <MapSection
                                markers={event?.locations?.map(
                                    (l) => l.location
                                )}
                                zoomLevel={10}
                                height={getMapHeight()}
                            />
                            <Paragraph className="paragraph addresses">
                                {event?.locations?.map((l) => {
                                    const { city, address, province, code } = l
                                    return (
                                        <a
                                            key={code}
                                            style={{ display: "block" }}
                                            href={getMapsLink(l)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {address}, {city}{" "}
                                            {province && `(${province})`}
                                        </a>
                                    )
                                })}
                            </Paragraph>
                        </div>
                    )}
                </div>
            </div>
            {eventSoldOut ? (
                <div className="sold-out">
                    <span>
                        I posti sono attualmente esauriti. Vi invitiamo a
                        verificare eventuali nuove disponibilità.
                    </span>
                </div>
            ) : (
                <BookingForm
                    slots={event?.slots}
                    className="slots"
                    eventId={eventId}
                    date={event?.date}
                    defaultBirth={birthDate}
                    refresh={fetchEvent}
                    event={event}
                    loginFulfilled={loginFulfilled}
                />
            )}
        </main>
    )

    if (loginFulfilled) {
        return <div className={props.className}>{body}</div>
    }

    return (
        <div className={props.className}>
            <LoginOverlay
                targetEvent={event}
                onSuccess={() => setLoginFulfilled(true)}
                onCancel={history.goBack}
                loading={loginLoading || loading}
            >
                {body}
            </LoginOverlay>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

EventDetail.propTypes = {
    className: PropTypes.string.isRequired,
    match: PropTypes.object,
}

EventDetail.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledEventDetail = styled(EventDetail)`
    & {
        padding-bottom: 60px;
        display: flex;
        justify-content: center;
        width: 100%;
        max-width: 100%;
        .screen-title {
            margin-top: 85px;
            margin-bottom: 45px;
        }
        main {
            max-width: 1980px;
            display: flex;
            flex-direction: column;
            align-items: "center";
            padding: 0px 110px;
            padding-bottom: 50px;
            .content {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                .left {
                    padding-right: 70px;
                    max-width: 1000px;
                    width: 60%;
                    min-width: 60%;
                    .detail-image {
                        margin-top: 20px;
                        width: 100%;
                        height: auto;
                    }
                    h1,
                    h2,
                    h3 {
                        font-family: "Poppins-Bold";
                        margin: 0;
                        padding: 0;
                        margin-bottom: 10px;
                    }
                    h1 {
                        font-family: Poppins-Bold;
                        font-size: 22px;
                    }
                    h2 {
                        font-size: 20px;
                    }
                    h3 {
                        font-size: 18px;
                    }
                    ul,
                    ol {
                        li {
                            font-family: "Poppins-Regular";
                            margin: 0;
                            padding: 0;
                            font-size: 16px;
                            color: ${({ theme }) => theme?.colors?.textMuted};
                            line-height: 170%;
                        }
                    }
                    .paragraph {
                        font-size: 16px;

                        a {
                            color: ${({ theme }) => theme.colors.textMuted};
                            text-decoration: underline;
                        }
                    }
                    .heading {
                        margin-bottom: 25px;
                        padding-top: 40px;
                    }
                    .eligibility {
                        .eligibility-header {
                        }

                        .booking {
                            margin-top: 40px;
                        }
                    }
                }
                .right {
                    min-width: 40%;
                    .map {
                        width: 100%;
                        .map-heading {
                            margin-bottom: 20px;
                        }
                        .paragraph {
                            margin-top: 10px;
                            font-size: 14px;
                            color: ${({ theme }) => theme?.colors?.textMuted};
                            text-align: right;
                            a {
                                color: ${({ theme }) => theme.colors.textMuted};
                                text-decoration: underline;
                            }
                        }
                        .addresses {
                            display: flex;
                            flex-direction: column;
                            gap: 6px;
                        }
                    }
                }
            }
            .slots {
                margin-top: 50px;
                width: 100%;
                /* padding: 0px 100px; */
            }
            .sold-out {
                margin-top: 30px;
                text-align: center;
                span {
                    font-size: 22px;
                    font-family: "Poppins-Bold";
                    color: ${({ theme }) => theme.colors.errorColor};
                }
            }
        }
        @media (max-width: 980px) {
            main {
                padding: 0px 70px;
                .content {
                    flex-direction: column;
                    .left {
                        width: 100%;
                        margin: 0;
                        padding: 0;
                    }
                    .right {
                        margin-top: 40px;
                        width: 100%;
                        .map {
                            .paragraph {
                                text-align: center;
                            }
                        }
                    }
                }
            }
        }
        @media (max-width: 768px) {
            main {
                max-width: 100vw;
                width: 100vw;
                padding: 0px 40px;
            }
        }
        @media (max-width: 576px) {
            .content {
                /* padding: 0px 15px; */
            }
            main {
                max-width: 100vw;
                padding: 0px 10px;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledEventDetail
